import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Header from '../components/Header';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

interface User { id: string; email: string; first_name: string; last_name: string; roles: Array<{ key: string; name: string; description: string }>; }

interface PricingRow {
  duration_months: number;
  margin_percentage: number;
  total_revenue: number;
  monthly_price: number;
  break_even_months: number;
  profit_at_end: number;
  breaks_even_in_term: boolean;
  revenue_per_day: number;
  profit_velocity: number | null;
}

interface InstanceROI {
  instance_id: string;
  instance_number: string;
  acquisition_cost: number | null;
  rental_income: number;
  roi_percentage: number | null;
  break_even_reached: boolean;
}

interface Analysis {
  template: { id: string; name: string; base_price: number; cost_price: number; rrp: number };
  supplier: { id: string; name: string; markup_percentage: number | null } | null;
  pricing_table: PricingRow[];
  efficiency_metrics: { recommended_duration: number | null; best_profit_velocity: number | null; margin_range: { min: number; max: number } | null };
  utilization: { avg_rate: number | null; total_instances: number; days_tracked: number };
  maintenance_summary: { avg_annual_cost: number | null; total_records: number };
  actual_roi: InstanceROI[];
  fleet_summary: { total_investment: number; total_income: number; fleet_roi_percentage: number | null };
}

interface TemplateOption { id: string; name: string; }

const RENTAL_DURATIONS = [1, 2, 3, 6, 12, 24, 36];
const tkn = () => localStorage.getItem('token');
const fmt = (v: number) => {
  const neg = v < 0;
  const s = Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return neg ? `-£${s}` : `£${s}`;
};

export default function BreakEvenAnalysis() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [selectedId, setSelectedId] = useState(searchParams.get('product') || '');
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAllUnits, setShowAllUnits] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (!tkn() || !savedUser) { navigate('/login'); return; }
    try { setUser(JSON.parse(savedUser)); } catch { navigate('/login'); }
    loadTemplates();
  }, []);

  useEffect(() => {
    if (selectedId) loadAnalysis(selectedId);
  }, [selectedId]);

  const loadTemplates = async () => {
    try {
      const res = await fetch('/api/asset-templates?limit=500', { headers: { Authorization: `Bearer ${tkn()}` } });
      if (res.ok) {
        const data = await res.json();
        setTemplates((data.data || []).map((t: any) => ({ id: t.id, name: t.name })));
      }
    } catch (err) { console.error(err); }
  };

  const loadAnalysis = useCallback(async (tid: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/break-even/template/${tid}`, { headers: { Authorization: `Bearer ${tkn()}` } });
      if (res.ok) {
        const data = await res.json();
        setAnalysis(data.data);
        setShowAllUnits(false);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="break-even" />
      <main className="container mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-3">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </button>

        <h1 className="text-3xl font-heading font-bold mb-1">Break-Even Analysis</h1>
        <p className="text-muted-foreground mb-6">Analyse rental profitability and find the break-even point for any product.</p>

        <div className="flex items-center gap-3 mb-8">
          <label className="text-sm font-medium">Product:</label>
          <select
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
            className="border rounded-md px-3 py-2 pr-8 text-sm bg-background min-w-[350px] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22currentColor%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_8px_center] bg-no-repeat"
          >
            <option value="">Select a product...</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>

        {loading && <div className="text-center py-8">Loading analysis...</div>}

        {analysis && !loading && (
          <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card><CardContent className="pt-6"><div className="text-xs text-muted-foreground">Cost (Trade)</div><div className="text-xl font-bold">{fmt(analysis.template.cost_price)}</div></CardContent></Card>
              <Card><CardContent className="pt-6"><div className="text-xs text-muted-foreground">RRP / Buy-out (+{analysis.supplier?.markup_percentage ?? 0}%)</div><div className="text-xl font-bold">{analysis.template.rrp > 0 ? fmt(analysis.template.rrp) : '—'}</div></CardContent></Card>
              <Card><CardContent className="pt-6"><div className="text-xs text-muted-foreground">Supplier</div><div className="text-xl font-bold">{analysis.supplier?.name || '—'}</div></CardContent></Card>
              <Card><CardContent className="pt-6"><div className="text-xs text-muted-foreground">Margin Range</div><div className="text-xl font-bold">{analysis.efficiency_metrics.margin_range ? `${analysis.efficiency_metrics.margin_range.min}%–${analysis.efficiency_metrics.margin_range.max}%` : '—'}</div></CardContent></Card>
              <Card><CardContent className="pt-6"><div className="text-xs text-muted-foreground">Best Plan</div><div className="text-xl font-bold">{analysis.efficiency_metrics.recommended_duration ? `${analysis.efficiency_metrics.recommended_duration} mo` : '—'}</div></CardContent></Card>
            </div>

            {/* Pricing per Duration */}
            {analysis.template.cost_price > 0 && (() => {
              const cost = analysis.template.cost_price;
              return (
              <Card>
                <CardHeader>
                  <CardTitle>Pricing per Duration</CardTitle>
                  <CardDescription>
                    Margin % of cost price ({fmt(cost)}). Shows current configured margins and break-even performance.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Duration</th>
                        <th className="text-right py-2">Current Margin</th>
                        <th className="text-right py-2">Monthly Price</th>
                        <th className="text-right py-2">Total Revenue</th>
                        <th className="text-right py-2">Profit/Loss</th>
                        <th className="text-right py-2">Break-Even</th>
                        <th className="text-center py-2">Covers Cost?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {RENTAL_DURATIONS.map(dur => {
                        const currentRow = analysis.pricing_table.find(p => p.duration_months === dur);
                        const coversCost = currentRow ? currentRow.total_revenue >= cost : false;
                        const diff = currentRow && currentRow.break_even_months > 0
                          ? currentRow.break_even_months - dur
                          : null;
                        return (
                          <tr key={dur} className="border-b last:border-0">
                            <td className="py-1.5">{dur} month{dur > 1 ? 's' : ''}</td>
                            <td className={`py-1.5 text-right font-mono ${currentRow ? '' : 'text-muted-foreground'}`}>
                              {currentRow ? `${currentRow.margin_percentage}%` : '—'}
                            </td>
                            <td className="py-1.5 text-right font-mono">
                              {currentRow ? fmt(currentRow.monthly_price) : '—'}
                            </td>
                            <td className="py-1.5 text-right font-mono">
                              {currentRow ? fmt(currentRow.total_revenue) : '—'}
                            </td>
                            <td className={`py-1.5 text-right font-mono ${currentRow ? (coversCost ? 'text-green-600' : 'text-red-500') : 'text-muted-foreground'}`}>
                              {currentRow ? `${currentRow.profit_at_end >= 0 ? '+' : ''}${fmt(currentRow.profit_at_end)}` : '—'}
                            </td>
                            <td className={`py-1.5 text-right font-mono ${diff !== null ? (diff <= 0 ? 'text-green-600' : 'text-red-500') : 'text-muted-foreground'}`}>
                              {diff !== null ? (diff <= 0 ? `${diff} ✓` : `+${diff} mo`) : '—'}
                            </td>
                            <td className="py-1.5 text-center">
                              {currentRow
                                ? coversCost
                                  ? <Badge variant="default">Yes</Badge>
                                  : <Badge variant="destructive">No</Badge>
                                : <span className="text-muted-foreground text-xs">Not set</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p className="text-xs text-muted-foreground mt-2">Break-Even = extra months beyond term to recoup cost. Negative with ✓ = recoups within term.</p>
                </CardContent>
              </Card>
              );
            })()}

            {/* Utilization & Maintenance */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle>Utilization</CardTitle></CardHeader>
                <CardContent className="text-sm space-y-2">
                  <div>Units: <strong>{analysis.utilization.total_instances}</strong></div>
                  <div>Current utilization: <strong>{analysis.utilization.avg_rate !== null ? `${analysis.utilization.avg_rate}%` : 'No data'}</strong></div>
                  <div>Days tracked: <strong>{analysis.utilization.days_tracked.toLocaleString()}</strong></div>
                  {analysis.utilization.avg_rate !== null && analysis.pricing_table.length > 0 && (
                    <div className="pt-2 border-t mt-3">
                      <p className="text-muted-foreground mb-1">Utilization-adjusted break-even (12-month plan):</p>
                      {(() => {
                        const row12 = analysis.pricing_table.find(p => p.duration_months === 12);
                        if (!row12 || !analysis.utilization.avg_rate) return <span>—</span>;
                        const adjusted = Math.ceil(row12.break_even_months / (analysis.utilization.avg_rate / 100));
                        return <span>Raw: {row12.break_even_months} mo → Adjusted: <strong>{adjusted} mo</strong> (+{adjusted - row12.break_even_months} mo)</span>;
                      })()}
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Maintenance</CardTitle></CardHeader>
                <CardContent className="text-sm space-y-2">
                  <div>Maintenance records: <strong>{analysis.maintenance_summary.total_records}</strong></div>
                  <div>Avg annual cost: <strong>{analysis.maintenance_summary.avg_annual_cost !== null ? fmt(analysis.maintenance_summary.avg_annual_cost) : 'No data'}</strong></div>
                </CardContent>
              </Card>
            </div>

            {/* Actual ROI */}
            {analysis.actual_roi.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Actual ROI per Unit</CardTitle>
                  <CardDescription>
                    Real rental income vs acquisition cost
                    {analysis.actual_roi.length > 10 && <span className="ml-1">· Showing 10 of {analysis.actual_roi.length} units</span>}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b"><th className="text-left py-2">Instance</th><th className="text-right py-2">Cost</th><th className="text-right py-2">Rental Income</th><th className="text-right py-2">ROI</th><th className="text-center py-2">Status</th></tr></thead>
                    <tbody>
                      {analysis.actual_roi.slice(0, showAllUnits ? undefined : 10).map(r => (
                        <tr key={r.instance_id} className="border-b last:border-0">
                          <td className="py-1.5">{r.instance_number}</td>
                          <td className="py-1.5 text-right font-mono">{r.acquisition_cost ? fmt(r.acquisition_cost) : '—'}</td>
                          <td className="py-1.5 text-right font-mono">{fmt(r.rental_income)}</td>
                          <td className={`py-1.5 text-right font-mono ${(r.roi_percentage || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {r.roi_percentage !== null ? `${r.roi_percentage}%` : '—'}
                          </td>
                          <td className="py-1.5 text-center">
                            {r.break_even_reached ? <Badge variant="default">Break-even ✓</Badge> : <Badge variant="secondary">In progress</Badge>}
                          </td>
                        </tr>
                      ))}
                      <tr className="font-medium border-t-2">
                        <td className="py-2">Fleet Total ({analysis.actual_roi.length} units)</td>
                        <td className="py-2 text-right font-mono">{fmt(analysis.fleet_summary.total_investment)}</td>
                        <td className="py-2 text-right font-mono">{fmt(analysis.fleet_summary.total_income)}</td>
                        <td className={`py-2 text-right font-mono ${(analysis.fleet_summary.fleet_roi_percentage || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {analysis.fleet_summary.fleet_roi_percentage !== null ? `${analysis.fleet_summary.fleet_roi_percentage}%` : '—'}
                        </td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                  {analysis.actual_roi.length > 10 && (
                    <button onClick={() => setShowAllUnits(prev => !prev)} className="text-sm text-primary hover:underline mt-3">
                      {showAllUnits ? 'Show less' : `View all ${analysis.actual_roi.length} units →`}
                    </button>
                  )}
                </CardContent>
              </Card>
            )}

            {analysis.supplier?.id && (
              <p className="text-sm text-muted-foreground">
                Margin policy and profitability planner:{' '}
                <button
                  type="button"
                  className="text-primary hover:underline font-medium"
                  onClick={() => navigate(`/suppliers/${analysis.supplier!.id}`)}
                >
                  {analysis.supplier.name}
                </button>
              </p>
            )}
          </div>
        )}

        {!analysis && !loading && !selectedId && (
          <div className="text-center py-16 text-muted-foreground">
            Select a product above to view its break-even analysis.
          </div>
        )}
      </main>
    </div>
  );
}
